import React from 'react';
import { Shield, FileText } from 'lucide-react';

export const Privacy = () => {
  return (
    <div className="min-h-screen py-20 bg-gradient-to-br from-hr-warm-bg via-white to-hr-purple-light/10 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-hr-purple/5 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-hr-teal/5 rounded-full blur-3xl animate-float-slow" />
      
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <article className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12 animate-fade-in-up">
          <header className="mb-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-hr-purple-light mb-6 animate-bounce-subtle">
              <Shield className="h-8 w-8 text-hr-purple" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-hr-text-primary mb-4 animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Privacy Policy
            </h1>
            <p className="text-hr-text-muted animate-fade-in-up stagger-2" style={{ opacity: 0 }}>Last Updated: 1/15/2026</p>
          </header>

          <div className="prose prose-lg max-w-none text-hr-text-secondary animate-fade-in-up stagger-3" style={{ opacity: 0 }}>
            <p className="lead">
              At HR Nest, we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and disclose personal information obtained through our websites, platforms, and services (collectively, the "Services").
            </p>

            <p>
              This Privacy Policy applies to HR Nest's websites, membership platforms, community spaces, digital resources, and related services (each a "Site" and collectively, the "Services"), which are owned and operated by HR Nest LLC ("HR Nest," "we," "our," or "us").
            </p>

            <p>
              This Policy applies to personal information processed in connection with your use of the Services and is incorporated into our Terms of Use and any applicable membership or subscriber agreements (collectively, the "Terms"). Capitalized terms not defined here are defined in the Terms.
            </p>

            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mt-12 mb-4">Updates to This Policy</h2>
            <p>
              We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements. The "Last Updated" date above indicates when this Policy was most recently revised.
            </p>
            <p>
              If we make material changes to how we use personal information, we will notify you prior to those changes taking effect. Notifications may occur via email, an in-platform notice, or a prominent notice on our Services, as required by law.
            </p>
            <p>
              Your continued use of the Services after updates become effective constitutes acceptance of the revised Privacy Policy.
            </p>

            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mt-12 mb-4">How to Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding privacy, please contact us at:<br />
              📧 HRnestllc@gmail.com
            </p>

            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mt-12 mb-4">When This Policy Applies</h2>
            <p>This Privacy Policy applies to personal information we collect directly from users of HR Nest Services.</p>
            <p>This Policy does not apply to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Third-party services:</strong> If you follow links to third-party websites or tools, their privacy policies govern your interactions.</li>
              <li><strong>Information we process on behalf of another entity:</strong> If HR Nest processes data as a service provider or processor for another organization under a separate agreement, that agreement controls.</li>
            </ul>

            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mt-12 mb-4">Information We Collect & Why We Use It</h2>
            <p>We collect and process personal information to operate, improve, and secure our Services.</p>

            <h3 className="font-heading text-xl font-semibold text-hr-text-primary mt-8 mb-3">Account Information</h3>
            <p><strong>What we collect:</strong> Name, email address, login credentials, account settings, membership status.</p>
            <p><strong>Why we collect it:</strong> To create and manage your account, to provide access to Services, to communicate with you.</p>
            <p><strong>Retention:</strong> For the life of your account or as required by law</p>

            <h3 className="font-heading text-xl font-semibold text-hr-text-primary mt-8 mb-3">Activity & Usage Data</h3>
            <p><strong>What we collect:</strong> Pages visited, interactions, timestamps, and engagement data.</p>
            <p><strong>Why we collect it:</strong> To improve Services, to prevent fraud and misuse, to understand user engagement.</p>
            <p><strong>Legal basis:</strong> Legitimate business interests and contractual performance</p>
            <p><strong>Retention:</strong> Periodically deleted or anonymized</p>

            <h3 className="font-heading text-xl font-semibold text-hr-text-primary mt-8 mb-3">Communication Data</h3>
            <p><strong>What we collect:</strong> Messages, emails, support requests, feedback, and survey responses.</p>
            <p><strong>Why we collect it:</strong> To respond to inquiries, to provide support, to improve Services, to send relevant updates (with opt-out options).</p>
            <p><strong>Legal basis:</strong> Legitimate interests and consent where required</p>
            <p><strong>Retention:</strong> Account lifetime or as required by law</p>

            <h3 className="font-heading text-xl font-semibold text-hr-text-primary mt-8 mb-3">Device & Technical Information</h3>
            <p><strong>What we collect:</strong> IP address, browser type, device identifiers, approximate location.</p>
            <p><strong>Why we collect it:</strong> Security and fraud prevention, platform functionality, analytics and diagnostics.</p>
            <p><strong>Legal basis:</strong> Legitimate business interests</p>
            <p><strong>Retention:</strong> Deleted periodically in the ordinary course of business</p>

            <h3 className="font-heading text-xl font-semibold text-hr-text-primary mt-8 mb-3">Payment Information</h3>
            <p><strong>What we collect:</strong> Limited payment identifiers (e.g., last four digits of card, transaction history).</p>
            <p><strong>Why we collect it:</strong> To process payments, to comply with legal and financial obligations.</p>
            <p><strong>Legal basis:</strong> Legal obligation and contractual necessity</p>
            <p><strong>Retention:</strong> As required by law</p>

            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mt-12 mb-4">Data Security Disclaimer</h2>
            <p>
              While we take reasonable administrative, technical, and organizational measures to protect personal information, no system can guarantee absolute security. You provide personal information at your own risk.
            </p>

            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mt-12 mb-4">Information We Share</h2>
            <p>We may disclose personal information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>In aggregated or de-identified form</li>
              <li>To trusted service providers (hosting, email, analytics, payment processors)</li>
              <li>To comply with legal obligations</li>
              <li>To protect rights, safety, or property</li>
              <li>In connection with a merger, sale, or restructuring</li>
            </ul>
            <p className="font-semibold mt-4">We do not sell personal information.</p>

            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mt-12 mb-4">Children's Privacy</h2>
            <p>
              HR Nest does not knowingly collect personal information from individuals under the age of 18. If you believe a child has provided us with personal information, please contact us immediately.
            </p>

            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mt-12 mb-4">Your Rights & Choices</h2>
            <p>You may:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Opt out of marketing communications</li>
              <li>Request access, correction, or deletion of your information</li>
              <li>Withdraw consent where applicable</li>
            </ul>
            <p>Requests may be submitted to HRnestllc@gmail.com</p>
            <p>We will verify your identity before processing requests and respond within timeframes required by applicable law.</p>

            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mt-12 mb-4">Cookies & Analytics</h2>
            <p>
              We use cookies, pixels, and similar technologies to improve functionality, analyze usage, and personalize experiences. You may manage cookie preferences through your browser settings.
            </p>
            <p>We may use third-party analytics services such as Google Analytics in accordance with their policies.</p>
            <p><strong>Consent:</strong> By continuing to use our website, you consent to our use of cookies in accordance with this policy unless you disable them.</p>

            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mt-12 mb-4">Third-Party Links</h2>
            <p>Our Services may include links to third-party websites. HR Nest is not responsible for the privacy practices of those sites.</p>

            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mt-12 mb-4">U.S. State Privacy Rights</h2>
            <p>
              Residents of states with consumer privacy laws (e.g., California, Colorado, Virginia, Connecticut) may have additional rights, including access, correction, deletion, and opting out of data sharing.
            </p>
            <p>
              HR Nest does not knowingly sell personal data and does not discriminate against users who exercise privacy rights.
            </p>

            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mt-12 mb-4">International Users</h2>
            <p>
              HR Nest operates from the United States. By using our Services, you consent to the transfer and processing of your information in the U.S. and other jurisdictions as permitted by law.
            </p>
            <p>
              Where applicable, HR Nest complies with GDPR and other international data protection regulations, including use of approved data transfer mechanisms.
            </p>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Privacy;
